import { useCallback, useEffect, useRef } from 'react'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { activeSellerSubscriptionServiceQuery } from '@b-stock/fe-core/bstock-next/queries'
import {
  AnalyticsEntityType,
  AnalyticsSource,
} from '@b-stock/fe-core/bstock-react'

import { getFeeModel } from '@/buyer-portal/helpers/sellerCollectsFeeModel'
import { useHasPriorSellerCollectsOrder } from '@/buyer-portal/helpers/useHasPriorSellerCollectsOrder'
import useIsLoggedIn from '@/buyer-portal/helpers/useIsLoggedIn'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import {
  SellerCollectsEvent,
  SellerCollectsProperty,
  SellerCollectsSurface,
  type SellerCollectsEventName,
  type SellerCollectsSurfaceName,
} from '@/helpers/telemetry/SellerCollectsAnalyticsEvents'

import { getListingSaleKind, type SaleKind } from './sellerCollectsSaleKind'

/**
 * What a Seller Collects event says about the listing or order it came from.
 *
 * Everything is optional except the two measures the PRD asks to segment on,
 * and both of those are three-state: a signed-out visitor cannot resolve the
 * seller's fee model, and the buyer's history is unknown until the lookup
 * settles. `null` says so rather than guessing `false`, which would quietly
 * count every anonymous listing view as a fee-free first-timer.
 */
export interface SellerCollectsEventContext {
  storefrontName?: string
  storefrontId?: string
  buyerPremiumApplies: boolean | null
  listingId?: string
  orderId?: string
  saleKind?: SaleKind
  surface?: SellerCollectsSurfaceName
  /**
   * Overrides the first-timer flag for a surface where the shared lookup means
   * something else. On an order page the order in view is itself a Seller
   * Collects order, so the buyer's order count always includes it and a
   * first-timer would otherwise report `false`.
   */
  isFirstSellerCollects?: boolean | null | undefined
}

/** `getFeeModel`'s answer as the one boolean every event carries. */
export const buyerPremiumAppliesFor = (
  feeModel: ReturnType<typeof getFeeModel>
): boolean | null => {
  if (feeModel === 'feeBearing') return true
  if (feeModel === 'feeFree') return false

  return null
}

/**
 * The one place a Seller Collects event's properties are assembled.
 *
 * Exported and used by every emit site rather than copied: the measures are
 * only computable if `storefront_name` means the same thing, and holds the
 * same type, on a listing view and on an order view. A property built per
 * call site drifts the first time two of them disagree.
 *
 * Keys are omitted rather than sent as `undefined` — Segment records an
 * explicit null, and "the storefront was not resolved" and "the storefront has
 * no name" are different facts.
 */
export const buildSellerCollectsProperties = (
  context: SellerCollectsEventContext & {
    isFirstSellerCollects: boolean | null
  }
): Record<string, string | boolean | null> => ({
  [SellerCollectsProperty.SellerCollects]: true,
  [SellerCollectsProperty.BuyerPremiumApplies]: context.buyerPremiumApplies,
  [SellerCollectsProperty.IsFirstSellerCollects]: context.isFirstSellerCollects,
  ...(context.storefrontName !== undefined && {
    [SellerCollectsProperty.StorefrontName]: context.storefrontName,
  }),
  ...(context.storefrontId !== undefined && {
    [SellerCollectsProperty.StorefrontId]: context.storefrontId,
  }),
  ...(context.listingId !== undefined && {
    [SellerCollectsProperty.ListingId]: context.listingId,
  }),
  ...(context.orderId !== undefined && {
    [SellerCollectsProperty.OrderId]: context.orderId,
  }),
  ...(context.saleKind !== undefined && {
    [SellerCollectsProperty.SaleKind]: context.saleKind,
  }),
  ...(context.surface !== undefined && {
    [SellerCollectsProperty.Surface]: context.surface,
  }),
})

/**
 * Emits a Seller Collects event, or nothing at all when the flag is off.
 *
 * The first-time flag is resolved here rather than passed in, so no call site
 * can report a buyer as new on one surface and returning on another.
 */
export const useSellerCollectsAnalytics = ({
  sellerAccountId,
  enabled = true,
  resolveBuyerHistory = true,
}: {
  /** The storefront in view, which scopes the first-time flag. */
  sellerAccountId?: string
  enabled?: boolean
  resolveBuyerHistory?: boolean
} = {}) => {
  const { track } = useAnalytics()
  const sellerCollectsEnabled = useFlag(
    FeatureFlags.SELLER_COLLECTS_BUYER_EXPERIENCE
  )
  // Nothing is emitted with the flag off, so nothing needs looking up either:
  // without this every search card would cost an order-count request.
  const isEnabled = sellerCollectsEnabled && enabled
  // An order surface supplies the flag itself, so looking the history up there
  // would cost a sessionStorage read and a query for an answer never read.
  // Per seller, like the banner and the first-win modal: the reported flag is
  // specified to follow the same rule as the surfaces it describes.
  const hasPriorOrder = useHasPriorSellerCollectsOrder({
    sellerAccountId,
    enabled: isEnabled && resolveBuyerHistory,
  })
  // A signed-out visitor's history is unknowable, not empty. The banner treats
  // them as new to the model on purpose, but counting an anonymous view as a
  // first-time buyer would inflate the very segment the bid-to-pay measure is
  // about — and that buyer has to sign in before they can complete it.
  const isLoggedIn = useIsLoggedIn()
  const buyerHistory = isLoggedIn ? hasPriorOrder : undefined

  const trackSellerCollectsEvent = useCallback(
    (event: SellerCollectsEventName, context: SellerCollectsEventContext) => {
      if (!isEnabled) return

      const { isFirstSellerCollects: override, ...rest } = context
      // Presence, not truthiness: a surface that supplies the flag owns it
      // even when its own lookup has not settled. Falling back there would
      // hand an order page the listing-side answer, which counts the order
      // being viewed and reads every first-timer as a returning buyer.
      // Only an actual boolean is inverted: the lookup answers `null` when the
      // count failed, and `!null` would report that as a first-time buyer.
      const isFirstSellerCollects =
        'isFirstSellerCollects' in context
          ? (override ?? null)
          : typeof buyerHistory === 'boolean'
            ? !buyerHistory
            : null

      track(event, {
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
        ...buildSellerCollectsProperties({ ...rest, isFirstSellerCollects }),
      })
    },
    [buyerHistory, isEnabled, track]
  )

  return {
    trackSellerCollectsEvent,
    /**
     * Whether an emit would actually go out. Callers that record "already
     * reported" must check it: the flag reads its `false` default until
     * LaunchDarkly resolves, so marking a listing reported before then would
     * swallow the event the moment the real value arrives.
     */
    isEnabled,
    /**
     * False while the buyer's history is still in flight. A signed-out visitor
     * never settles: there is no history to resolve, and `null` is the answer.
     */
    isBuyerHistorySettled: !isLoggedIn || hasPriorOrder !== undefined,
  }
}

/** What the analytics need from a listing, in one shape both LDP tag rows build. */
export interface SellerCollectsListingSource {
  /**
   * Required: the view is reported once per listing id, and an optional one
   * would match the "already reported" ref's initial `undefined` and silence
   * the event for good. Every listing-details shape carries it.
   */
  _id: string
  /**
   * Required for the same reason: the first-time flag is now per seller, and
   * the view is held until it settles — which it never does without an id.
   */
  accountId: string
  seller?: { storefront?: { _id?: string; name?: string } | null }
}

/**
 * The listing half of the property set.
 *
 * `hasBuyerPremium` arrives from `useListingTagsModel`, which is also what the
 * tag's own copy branches on — so what the event reports and what the buyer
 * was shown cannot disagree.
 */
export const sellerCollectsListingContext = (
  listing: SellerCollectsListingSource,
  hasBuyerPremium: boolean | null | undefined
): SellerCollectsEventContext => ({
  storefrontName: listing.seller?.storefront?.name,
  storefrontId: listing.seller?.storefront?._id,
  buyerPremiumApplies: hasBuyerPremium ?? null,
  listingId: listing._id,
  saleKind: getListingSaleKind(listing),
})

/**
 * Reports one view per Seller Collects listing.
 *
 * Held until the seller's fee model settles, or until it is known that it
 * never will — a signed-out visitor cannot resolve the plan at all, and an
 * errored lookup never resolves either. `isBuyerPremiumSettled` is the single
 * answer to that question; re-deriving it here let the two drift. Firing on
 * mount instead would record `buyer_premium_applies: null` for nearly every
 * signed-in view and make the property useless for segmenting.
 *
 * A signed-out visitor settles immediately, since nothing can resolve. That
 * rests on `useIsLoggedIn` being a read of the auth context rather than a
 * fetch, which it is — but the context does flip to signed-in when an expired
 * token refreshes. A view caught inside that window reports both properties as
 * `null`, and the once-per-listing guard keeps the corrected values out.
 * Re-reporting on the flip would double-count the view, which is worse for a
 * denominator than one unknown row, so it is left to be confirmed in the
 * Segment debugger on a hard reload while signed in.
 */
export const useSellerCollectsListingViewed = (
  listing: SellerCollectsListingSource,
  {
    isSellerCollects,
    hasBuyerPremium,
    isBuyerPremiumSettled,
  }: {
    isSellerCollects: boolean
    hasBuyerPremium: boolean | undefined
    /**
     * `useListingTagsModel`: false only while the plan is in flight. An
     * errored lookup is settled and unknown — waiting on it would drop the
     * view entirely and skew the denominator the measure is read against.
     */
    isBuyerPremiumSettled: boolean
  }
) => {
  const { trackSellerCollectsEvent, isBuyerHistorySettled, isEnabled } =
    useSellerCollectsAnalytics({
      sellerAccountId: listing.accountId,
      enabled: isSellerCollects,
    })
  const reportedListingId = useRef<string | undefined>(undefined)

  useEffect(() => {
    // `isEnabled` before the ref is written, not after: a signed-out visitor
    // settles both lookups instantly, so without this the first render marks
    // the listing reported while the flag still reads its default.
    if (!isEnabled || !isBuyerPremiumSettled || !isBuyerHistorySettled) return
    if (reportedListingId.current === listing._id) return
    reportedListingId.current = listing._id

    trackSellerCollectsEvent(
      SellerCollectsEvent.ListingViewed,
      sellerCollectsListingContext(listing, hasBuyerPremium)
    )
  }, [
    hasBuyerPremium,
    isBuyerHistorySettled,
    isBuyerPremiumSettled,
    isEnabled,
    listing,
    trackSellerCollectsEvent,
  ])

  // Handed back so the row reports its own events through this instance
  // rather than mounting the hook, and the lookup behind it, a second time.
  return { trackSellerCollectsEvent }
}

/** What the analytics need from an order. */
export interface SellerCollectsOrderSource {
  _id: string
  listing: {
    /** Required: the seller's plan query takes it, and an order always has one. */
    accountId: string
    seller?: { storefront?: { _id?: string; name?: string } | null }
  }
}

/** The order half of the property set, built like the listing half. */
export const sellerCollectsOrderContext = (
  order: SellerCollectsOrderSource,
  hasBuyerPremium: boolean | null | undefined
): SellerCollectsEventContext => ({
  storefrontName: order.listing.seller?.storefront?.name,
  storefrontId: order.listing.seller?.storefront?._id,
  buyerPremiumApplies: hasBuyerPremium ?? null,
  orderId: order._id,
})

/**
 * The seller's fee posture for an order, from the same query and the same
 * derivation the pay-the-seller card and the sticky bar use — so an event
 * cannot report a premium the page never showed.
 */
export const useSellerCollectsOrderFeeModel = (
  order: SellerCollectsOrderSource,
  isSellerCollects: boolean
): boolean | null | undefined => {
  const { data, isLoading, isError } =
    activeSellerSubscriptionServiceQuery.useQuery(order.listing.accountId, {
      enabled: isSellerCollects,
    })

  const feeModel = getFeeModel({
    isLoggedIn: true,
    isLoading,
    isError,
    buyersPremium: data?.buyersPremium,
  })

  // `undefined` means "not resolved yet", which a caller must wait for;
  // `null` means resolved to genuinely unknown. Collapsing the two would
  // report the plan as unknown on every order view before the query lands —
  // and an errored lookup, which `getFeeModel` also answers `undefined`, would
  // make a caller that waits for it wait for ever.
  if (isError) return null

  return feeModel === undefined ? undefined : buyerPremiumAppliesFor(feeModel)
}

/**
 * Reports an order-scoped event once per order.
 *
 * Keyed on the order id rather than on mount: the order page is reused across
 * a client-side hop from one order to the next, so a mount-scoped guard would
 * drop the second order's event entirely.
 */
export const useSellerCollectsOrderEventOnce = (
  event: SellerCollectsEventName,
  {
    isActive,
    order,
    hasBuyerPremium,
    isFirstSellerCollects,
  }: {
    isActive: boolean
    order: SellerCollectsOrderSource
    hasBuyerPremium: boolean | null | undefined
    /**
     * `useIsOnlySellerCollectsOrder`: undefined until the count lands, null
     * when it failed — a settled answer of "unknown" that must not be waited on.
     */
    isFirstSellerCollects: boolean | null | undefined
  }
) => {
  const { trackSellerCollectsEvent } = useSellerCollectsAnalytics({
    enabled: isActive,
    resolveBuyerHistory: false,
  })
  const reportedOrderId = useRef<string | undefined>(undefined)

  // Both answers are awaited rather than defaulted: the event fires once per
  // order, so reporting before they land would record `null` for good.
  const isSettled =
    hasBuyerPremium !== undefined && isFirstSellerCollects !== undefined

  useEffect(() => {
    if (!isActive || !isSettled || reportedOrderId.current === order._id) return
    reportedOrderId.current = order._id

    trackSellerCollectsEvent(event, {
      ...sellerCollectsOrderContext(order, hasBuyerPremium),
      isFirstSellerCollects,
    })
  }, [
    event,
    hasBuyerPremium,
    isActive,
    isFirstSellerCollects,
    isSettled,
    order,
    trackSellerCollectsEvent,
  ])
}

/**
 * The search half of the property set, for the grid and the List View.
 *
 * `buyer_premium_applies` is always unknown here: the fee posture comes from
 * the seller's subscription, and resolving it per card would mean a request
 * per storefront on the page — which is exactly why the tag's own copy makes
 * no fee claim on these surfaces either.
 */
export const sellerCollectsSearchResultContext = (
  listing: {
    listingId?: string
    storefrontId?: string
    storefrontName?: string
  },
  saleKind: SaleKind
): SellerCollectsEventContext => ({
  storefrontName: listing.storefrontName,
  storefrontId: listing.storefrontId,
  buyerPremiumApplies: null,
  listingId: listing.listingId,
  saleKind,
})

/**
 * Everything the listing-page tag rows need: the view is reported, and the
 * explainer handler comes back ready to hand to the tag.
 *
 * One hook because V1 and V2 render the same tag with the same wiring, and
 * until V1 is retired two copies are two chances for them to drift.
 */
export const useSellerCollectsListingTagAnalytics = (
  listing: SellerCollectsListingSource,
  model: {
    isSellerCollects: boolean
    hasBuyerPremium: boolean | undefined
    isBuyerPremiumSettled: boolean
  }
) => {
  const { trackSellerCollectsEvent } = useSellerCollectsListingViewed(
    listing,
    model
  )

  const onExplainerOpened = useCallback(() => {
    trackSellerCollectsEvent(SellerCollectsEvent.ExplainerOpened, {
      ...sellerCollectsListingContext(listing, model.hasBuyerPremium),
      surface: SellerCollectsSurface.ListingDetails,
    })
  }, [listing, model.hasBuyerPremium, trackSellerCollectsEvent])

  return { onExplainerOpened }
}
